import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Save, ListChecks } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Select } from "@/components/common";
import { OptionListEditor } from "@/components/OptionListEditor";

const AVAIL = ["Available", "Busy", "Unavailable"];

export function RosterSettings({ st, onSaved }) {
  const { user, can } = useApp();
  const isAdmin = user.role === "admin";
  const [members, setMembers] = useState([]);
  const [svcOpen, setSvcOpen] = useState(false);
  useEffect(() => { setMembers(st.members.map(({ user_id, name, paused, availability, start_time, end_time }) => ({ user_id, name, paused, availability, start_time, end_time }))); }, [st]);
  const set = (i, k, v) => setMembers(members.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const move = (i, d) => { const l = [...members]; [l[i], l[i + d]] = [l[i + d], l[i]]; setMembers(l); };
  const toggle = (s, on) => setMembers(on ? [...members, { user_id: s.id, name: s.name, paused: false, availability: "Available", start_time: null, end_time: null }] : members.filter((m) => m.user_id !== s.id));
  const save = async () => {
    try { await api.put(`/rotation/config/${st.location.id}`, { members }); toast.success("Rotation settings saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
      <section className="panel p-4" data-testid="roster-staff">
        <h3 className="mb-1 font-semibold text-[#20336F] dark:text-blue-200">Employees at {st.location.name}</h3>
        <p className="mb-3 text-xs text-[var(--muted-ink)]">From the existing employee records. Check to include in the daily rotation.</p>
        {st.staff.map((s) => (
          <label key={s.id} className="flex items-center gap-2 py-1 text-sm">
            <input type="checkbox" checked={members.some((m) => m.user_id === s.id)} onChange={(e) => toggle(s, e.target.checked)} data-testid={`roster-include-${s.id}`} />
            {s.name} <span className="text-[11px] capitalize text-[var(--muted-ink)]">{s.role === "employee" ? "Wellness Navigator" : s.role}</span>
          </label>
        ))}
        {!st.staff.length && <p className="text-sm text-[var(--muted-ink)]">No employees are assigned to this location. Assign locations on the Employees page.</p>}
        {can("action:manage_settings") && <button className="btn btn-ghost btn-sm mt-4" onClick={() => setSvcOpen(true)} data-testid="manage-services-btn"><ListChecks size={14} />Manage service list</button>}
      </section>
      <section className="panel p-4" data-testid="roster-order">
        <div className="mb-3 flex items-center justify-between">
          <div><h3 className="font-semibold text-[#20336F] dark:text-blue-200">Rotation order & eligibility</h3>
            <p className="text-xs text-[var(--muted-ink)]">Testing hours are Eastern Time. Leave blank for no limit (e.g. only one employee without an end time can test after 5:00 PM).{!isAdmin && " Only administrators can reorder."}</p></div>
          <button className="btn btn-primary btn-sm" onClick={save} data-testid="roster-save-btn"><Save size={14} />Save</button>
        </div>
        <div className="overflow-x-auto">
          <table className="tbl w-full">
            <thead><tr><th>#</th><th>Employee</th><th>Availability</th><th>Participating</th><th>Testing from</th><th>Testing until</th>{isAdmin && <th>Order</th>}</tr></thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={m.user_id} data-testid={`roster-row-${i}`}>
                  <td>{i + 1}</td><td className="font-medium">{m.name}</td>
                  <td><Select className="h-8 w-36" value={m.availability} onChange={(v) => set(i, "availability", v)} options={AVAIL} testid={`roster-avail-${i}`} /></td>
                  <td><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!m.paused} onChange={(e) => set(i, "paused", !e.target.checked)} data-testid={`roster-active-${i}`} />{m.paused ? "Paused" : "Active"}</label></td>
                  <td><input type="time" className="inp h-8 w-28" value={m.start_time || ""} onChange={(e) => set(i, "start_time", e.target.value || null)} data-testid={`roster-start-${i}`} /></td>
                  <td><input type="time" className="inp h-8 w-28" value={m.end_time || ""} onChange={(e) => set(i, "end_time", e.target.value || null)} data-testid={`roster-end-${i}`} /></td>
                  {isAdmin && <td><button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30" data-testid={`roster-up-${i}`}><ArrowUp size={14} /></button>
                    <button disabled={i === members.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30" data-testid={`roster-down-${i}`}><ArrowDown size={14} /></button></td>}
                </tr>
              ))}
              {!members.length && <tr><td colSpan={7} className="py-8 text-center text-[var(--muted-ink)]">No employees in this rotation yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <OptionListEditor open={svcOpen} onClose={() => setSvcOpen(false)} title="Predefined testing services" items={st.services} testid="services-editor"
        onSave={async (l) => { await api.put("/rotation/services", { services: l }); onSaved(); }} />
    </div>
  );
}
